import React, { useState } from 'react';
import { Copy, Check, Mail, Sparkles, RefreshCw, AlertCircle } from 'lucide-react';
import EmailSharing from './EmailSharing';
import ExpansionSuggestions from './ExpansionSuggestions';

const EmailOutput = ({
  email,
  isLoading,
  className = '',
  offerSnapshot = null,
  feedbackState = {},
  onImproveClick,
  onSuggestionClick,
  onCancelFeedback
}) => {
  const [copied, setCopied] = useState(false);
  const [copiedSection, setCopiedSection] = useState(''); // Track which section was copied
  const [copyError, setCopyError] = useState(false);

  // Robust copy with fallback for mobile/older browsers
  const copyToClipboard = async (text, section = 'full') => {
    setCopyError(false);

    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older browsers/non-HTTPS
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);

        if (!successful) {
          throw new Error('Fallback copy failed');
        }
      }

      // Success feedback
      setCopied(true);
      setCopiedSection(section);

      // Longer timeout for mobile users (3 seconds)
      setTimeout(() => {
        setCopied(false);
        setCopiedSection('');
      }, 3000);
    } catch (err) {
      console.error('Failed to copy:', err);
      setCopyError(true);
      // Clear error after 3 seconds
      setTimeout(() => setCopyError(false), 3000);
    }
  };

  const copyFullEmail = () => {
    const fullEmail = `Subject: ${email.subject}\n\n${email.hook}\n\n${email.body}${email.cta ? `\n\n${email.cta}` : ''}`;
    copyToClipboard(fullEmail, 'full');
  };

  if (isLoading) {
    return (
      <div className={`card ${className}`}>
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="relative">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-600 mx-auto mb-6"></div>
              <Sparkles className="absolute inset-0 w-5 h-5 text-primary-600 animate-pulse m-auto" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Crafting your email...</h3>
            <p className="text-gray-600">Using AI to transform your story into compelling content</p>
          </div>
        </div>
      </div>
    );
  }

  if (!email.subject) {
    return (
      <div className={`card ${className}`}>
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-500 mb-2">Your email will appear here</h3>
            <p className="text-gray-400">Enter your story and click generate to get started</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`card ${className}`}>
      {/* Email Sharing Section - At the top */}
      <div className="mb-6">
        <EmailSharing email={email} offerSnapshot={offerSnapshot} />
      </div>

      <div className="flex items-center justify-between mb-6 animate-slide-up">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center animate-bounce">
            <Mail className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">🎉 Email Generated!</h2>
            <p className="text-sm text-gray-600">Ready to copy and use</p>
          </div>
        </div>
        <button
          onClick={copyFullEmail}
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
            copyError
              ? 'bg-red-100 text-red-700 border border-red-300'
              : copied && copiedSection === 'full'
                ? 'bg-green-100 text-green-700 border border-green-300'
                : 'bg-primary-600 hover:bg-primary-700 text-white hover:shadow-lg transform hover:scale-105'
          }`}
        >
          {copyError ? (
            <>
              <AlertCircle className="w-4 h-4" />
              <span>Copy failed</span>
            </>
          ) : copied && copiedSection === 'full' ? (
            <>
              <Check className="w-4 h-4" />
              <span>Copied!</span>
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />
              <span>Copy All</span>
            </>
          )}
        </button>
      </div>

      {/* Copy Error Message - Mobile friendly */}
      {copyError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-700">
              Couldn't copy automatically. Please select the text and copy manually.
            </p>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* Primary Subject Line */}
        <div className="animate-slide-up">
          <label className="block text-sm font-semibold text-gray-700 mb-3 flex items-center space-x-2">
            <span>🎯 Primary Subject Line</span>
          </label>
          <div className="bg-gradient-to-r from-primary-50 to-blue-50 border-2 border-primary-200 rounded-xl p-5 relative group hover:shadow-md transition-all duration-200">
            <p className="text-primary-900 font-semibold text-lg leading-relaxed">{email.subject}</p>
            <button
              onClick={() => copyToClipboard(email.subject)}
              className="absolute top-3 right-3 p-2 bg-white/80 hover:bg-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            >
              <Copy className="w-4 h-4 text-primary-600" />
            </button>
          </div>
        </div>

        {/* Alternative Subject Lines */}
        <div className="animate-slide-up">
          <label className="block text-sm font-semibold text-gray-700 mb-4 flex items-center space-x-2">
            <span>🎨 Alternative Subject Lines</span>
            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">Choose your favorite</span>
          </label>
          <div className="space-y-3">
            {/* FOMO Subject Line */}
            {email.subjectLines?.fomo && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-2">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-medium text-red-700 bg-red-100 px-1.5 py-0.5 rounded">FOMO</span>
                    <p className="text-red-900 mt-1 text-sm leading-tight">{email.subjectLines.fomo}</p>
                  </div>
                  <button
                    onClick={() => copyToClipboard(email.subjectLines.fomo)}
                    className="text-xs text-red-600 hover:text-red-700 ml-2 flex-shrink-0"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )}

            {/* Curiosity Subject Line */}
            {email.subjectLines?.curiosity && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-medium text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">Curiosity</span>
                    <p className="text-blue-900 mt-1 text-sm leading-tight">{email.subjectLines.curiosity}</p>
                  </div>
                  <button
                    onClick={() => copyToClipboard(email.subjectLines.curiosity)}
                    className="text-xs text-blue-600 hover:text-blue-700 ml-2 flex-shrink-0"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )}

            {/* Value Subject Line */}
            {email.subjectLines?.value && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-2">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-medium text-green-700 bg-green-100 px-1.5 py-0.5 rounded">Value</span>
                    <p className="text-green-900 mt-1 text-sm leading-tight">{email.subjectLines.value}</p>
                  </div>
                  <button
                    onClick={() => copyToClipboard(email.subjectLines.value)}
                    className="text-xs text-green-600 hover:text-green-700 ml-2 flex-shrink-0"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )}

            {/* Question Subject Line */}
            {email.subjectLines?.question && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-2">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-medium text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded">Question</span>
                    <p className="text-purple-900 mt-1 text-sm leading-tight">{email.subjectLines.question}</p>
                  </div>
                  <button
                    onClick={() => copyToClipboard(email.subjectLines.question)}
                    className="text-xs text-purple-600 hover:text-purple-700 ml-2 flex-shrink-0"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )}

            {/* Special Subject Line */}
            {email.subjectLines?.special && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-medium text-yellow-700 bg-yellow-100 px-1.5 py-0.5 rounded">Special</span>
                    <p className="text-yellow-900 mt-1 text-sm leading-tight">{email.subjectLines.special}</p>
                  </div>
                  <button
                    onClick={() => copyToClipboard(email.subjectLines.special)}
                    className="text-xs text-yellow-600 hover:text-yellow-700 ml-2 flex-shrink-0"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Primary Hook */}
        <div className="animate-slide-up">
          <label className="block text-sm font-semibold text-gray-700 mb-3 flex items-center space-x-2">
            <span>Primary Hook</span>
          </label>
          <div className="bg-gradient-to-r from-gray-50 to-blue-50 border-2 border-gray-200 rounded-xl p-5 relative group hover:shadow-md transition-all duration-200">
            <p className="text-gray-900 font-medium leading-relaxed">{email.hook}</p>
            <button
              onClick={() => copyToClipboard(email.hook)}
              className="absolute top-3 right-3 p-2 bg-white/80 hover:bg-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            >
              <Copy className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Alternative Hooks */}
        {email.hooks && (email.hooks.story || email.hooks.question || email.hooks.outcome) && (
          <div className="animate-slide-up">
            <label className="block text-sm font-semibold text-gray-700 mb-4 flex items-center space-x-2">
              <span>Alternative Hooks</span>
              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">Choose your favorite</span>
            </label>
            <div className="space-y-3">
              {/* Story Hook */}
              {email.hooks.story && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-medium text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded">STORY</span>
                      <p className="text-indigo-900 mt-1.5 text-sm leading-relaxed">{email.hooks.story}</p>
                    </div>
                    <button
                      onClick={() => copyToClipboard(email.hooks.story)}
                      className="text-xs text-indigo-600 hover:text-indigo-700 ml-2 flex-shrink-0"
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              )}

              {/* Question Hook */}
              {email.hooks.question && (
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-medium text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded">QUESTION</span>
                      <p className="text-teal-900 mt-1.5 text-sm leading-relaxed">{email.hooks.question}</p>
                    </div>
                    <button
                      onClick={() => copyToClipboard(email.hooks.question)}
                      className="text-xs text-teal-600 hover:text-teal-700 ml-2 flex-shrink-0"
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              )}

              {/* Outcome Hook */}
              {email.hooks.outcome && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-medium text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded">OUTCOME</span>
                      <p className="text-orange-900 mt-1.5 text-sm leading-relaxed">{email.hooks.outcome}</p>
                    </div>
                    <button
                      onClick={() => copyToClipboard(email.hooks.outcome)}
                      className="text-xs text-orange-600 hover:text-orange-700 ml-2 flex-shrink-0"
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Email Body */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Body
          </label>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="whitespace-pre-wrap text-gray-900">{email.body}</div>
          </div>
          <button
            onClick={() => copyToClipboard(email.body)}
            className="mt-2 text-xs text-primary-600 hover:text-primary-700 flex items-center space-x-1"
          >
            <Copy className="w-3 h-3" />
            <span>Copy body</span>
          </button>
        </div>

        {/* CTA */}
        {email.cta && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Call to Action
            </label>
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
              <p className="text-primary-900 font-medium">{email.cta}</p>
            </div>
            <button
              onClick={() => copyToClipboard(email.cta)}
              className="mt-2 text-xs text-primary-600 hover:text-primary-700 flex items-center space-x-1"
            >
              <Copy className="w-3 h-3" />
              <span>Copy CTA</span>
            </button>
          </div>
        )}
      </div>

      {/* Feedback Section */}
      {!feedbackState.showFeedbackPanel && !feedbackState.isAnalyzing && !feedbackState.isExpanding && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="flex flex-col items-center space-y-3">
            <p className="text-sm text-primary-600 font-medium">
              Not quite right? Let's improve it.
            </p>
            <button
              onClick={onImproveClick}
              className="flex items-center space-x-2 px-5 py-2.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors shadow-sm"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Improve This Email</span>
            </button>
          </div>
        </div>
      )}

      {/* Expansion Suggestions */}
      {(feedbackState.showFeedbackPanel || feedbackState.isAnalyzing || feedbackState.isExpanding) && (
        <ExpansionSuggestions
          suggestions={feedbackState.suggestions || []}
          isAnalyzing={feedbackState.isAnalyzing}
          isExpanding={feedbackState.isExpanding}
          selectedSuggestion={feedbackState.selectedSuggestion}
          onSuggestionClick={onSuggestionClick}
          onCancel={onCancelFeedback}
        />
      )}
    </div>
  );
};

export default EmailOutput; 